import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Autocomplete,
  CircularProgress,
  InputAdornment,
} from "@avenue-8/sidekick-ui";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SearchIcon from "@mui/icons-material/Search";
import { TerritoryItem } from "@shared/apis/sidekick/generated";
import { useWidgetsGetTerritorySuggestions } from "@cma/shared/queries/useWidgetsGetTerritorySuggestions";
import { useDebouncedCallback } from "use-debounce";
import {
  formatTerritoryDisplayName,
  formatTerritoryOptionLabel,
  getTerritoryTypeLabel,
  TERRITORY_ALIAS_SEPARATOR,
} from "./territory-search-bar-suggestions-helpers";

interface TerritorySearchBarSuggestionsProps {
  value?: TerritoryItem | null;
  onChange?: (value: TerritoryItem | null) => void;
  placeholder?: string;
  disabled?: boolean;
  onSuggestionClick?: (suggestion: TerritoryItem) => void;
}

export const TerritorySearchBarSuggestions = (
  props: TerritorySearchBarSuggestionsProps,
) => {
  const {
    value,
    onChange,
    placeholder = "Search cities, neighborhoods, counties...",
    disabled = false,
    onSuggestionClick,
  } = props;
  const [inputValue, setInputValue] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");

  const debouncedSetQuery = useDebouncedCallback((query: string) => {
    setDebouncedQuery(query.length >= 1 ? query : "");
  }, 500);

  const handleInputChange = (
    _event: React.SyntheticEvent,
    newInputValue: string,
  ) => {
    setInputValue(newInputValue);
    debouncedSetQuery(newInputValue);
  };

  useEffect(() => {
    setInputValue(value ? formatTerritoryDisplayName(value) : "");
  }, [value]);

  const { data: suggestionsResponse, isLoading } =
    useWidgetsGetTerritorySuggestions({
      query: debouncedQuery,
    });

  const groupedOptions = useMemo(() => {
    return suggestionsResponse?.suggestions ?? [];
  }, [suggestionsResponse]);

  const handleChange = (
    _event: React.SyntheticEvent,
    newValue: TerritoryItem | string | null,
  ) => {
    if (typeof newValue === "string" || newValue === null) return;
    onChange?.(newValue);
    onSuggestionClick?.(newValue);
  };

  return (
    <Autocomplete
      freeSolo
      options={groupedOptions}
      groupBy={(option) => getTerritoryTypeLabel(option.type)}
      value={(value ?? null) as any}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      getOptionLabel={(option) =>
        typeof option === "string" ? option : formatTerritoryDisplayName(option)
      }
      isOptionEqualToValue={(option, value) =>
        option.name.split(TERRITORY_ALIAS_SEPARATOR)[0].trim() ===
          value.name.split(TERRITORY_ALIAS_SEPARATOR)[0].trim() &&
        option.state === value.state &&
        option.type === value.type &&
        option.city === value.city
      }
      filterOptions={(options) => options}
      loading={isLoading && debouncedQuery.length > 0}
      disabled={disabled}
      disableClearable
      noOptionsText={
        debouncedQuery.length === 0
          ? "Start typing to search..."
          : "No locations found"
      }
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <InputAdornment position="start" sx={{ color: "inherit" }}>
                <SearchIcon />
                {params.InputProps.endAdornment}
              </InputAdornment>
            ),
          }}
          hideHelperTextSpace
        />
      )}
      loadingText={
        <Box sx={{ px: 2, py: 12, display: "flex", justifyContent: "center" }}>
          <CircularProgress color="inherit" size={24} />
        </Box>
      }
      renderOption={({ key: _key, ...rest }, option) => (
        <Box
          component="li"
          key={`${option.type}-${option.name}-${option.state}-${option.city || ""}`}
          {...rest}
        >
          <LocationOnIcon fontSize="small" />
          <Box sx={{ ml: 1 }}>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {formatTerritoryOptionLabel(option)}
            </Typography>
          </Box>
        </Box>
      )}
    />
  );
};
